import { useCallback, useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import LeadsService from '@/services/LeadsService'

const escapeHtml = text =>
	`${text ?? ''}`.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char])

const normalizeCampaignList = items => {
	if (!Array.isArray(items)) return []
	const unique = new Map()
	items.forEach(item => {
		if (typeof item !== 'string') return
		const trimmed = item.trim()
		if (!trimmed) return
		const key = trimmed.toLowerCase()
		if (!unique.has(key)) unique.set(key, trimmed)
	})
	return Array.from(unique.values()).sort((a, b) => a.localeCompare(b))
}

export const useCampaignFilter = updateFilters => {
	const [selectedCampaign, setSelectedCampaign] = useState('')
	const [campaigns, setCampaigns] = useState([])
	const [loadingCampaigns, setLoadingCampaigns] = useState(false)
	const [campaignError, setCampaignError] = useState(null)

	const loadCampaigns = useCallback(async () => {
		setLoadingCampaigns(true)
		setCampaignError(null)
		try {
			const { data, error } = await LeadsService.getCampaigns()
			if (error) throw new Error(error)
			const cleaned = normalizeCampaignList(data)
			setCampaigns(cleaned)
			return cleaned
		} catch (err) {
			const message = err.message || 'Unable to load campaigns'
			setCampaignError(message)
			setCampaigns([])
			return []
		} finally {
			setLoadingCampaigns(false)
		}
	}, [])

	useEffect(() => {
		loadCampaigns()
	}, [loadCampaigns])

	const handleCampaignFilter = useCallback(async () => {
		let campaignPool = campaigns
		if (!campaignPool.length && !loadingCampaigns) {
			campaignPool = await loadCampaigns()
		}

		if (!campaignPool.length) {
			Swal.fire({ title: 'No campaigns available', text: campaignError || 'Try again later.', icon: 'info' })
			return
		}

		let pendingSelection = selectedCampaign

		const filterCampaigns = search => {
			if (!search) return campaignPool
			const term = search.toLowerCase()
			return campaignPool.filter(name => name.toLowerCase().includes(term))
		}

		const buildRows = list => {
			if (!list.length) {
				return '<tr><td colspan="2" style="padding:0.75rem; text-align:center; color:#6b7280;">No campaigns found</td></tr>'
			}
			return list
				.map((name, idx) => {
					const encodedValue = encodeURIComponent(name)
					const isSelected = pendingSelection && pendingSelection.toLowerCase() === name.toLowerCase()
					return `
						<tr class="campaign-row${isSelected ? ' selected' : ''}" data-value="${encodedValue}">
							<td style="width:60px; padding:0.5rem; border-bottom:1px solid #e5e7eb; text-align:center; color:#6b7280;">${idx + 1}</td>
							<td style="padding:0.5rem; border-bottom:1px solid #e5e7eb;">${escapeHtml(name)}</td>
						</tr>
					`
				})
				.join('')
		}

		const popupHtml = `
			<style>
				.campaign-search { width: 100%; padding: 0.6rem 0.75rem; border: 1px solid #d1d5db; border-radius: 0.5rem; }
				.campaign-table { width: 100%; border-collapse: collapse; margin-top: 0.75rem; }
				.campaign-row { cursor:pointer; transition: background-color 0.15s ease; }
				.campaign-row:hover { background-color:#f9fafb; }
				.campaign-row.selected { background-color:#dbeafe; }
				.campaign-wrapper { max-height: 16rem; overflow-y: auto; border: 1px solid #e5e7eb; border-radius: 0.75rem; margin-top:0.75rem; }
				.campaign-selected-label { margin-top:0.5rem; font-size:0.85rem; color:#374151; }
			</style>
			<div style="text-align:left;">
				<input id="campaign-search" class="campaign-search" type="text" placeholder="Search campaigns..." />
				<div id="campaign-selected-label" class="campaign-selected-label"></div>
				<div class="campaign-wrapper">
					<table class="campaign-table">
						<thead>
							<tr>
								<th style="width:60px; padding:0.4rem; text-align:center; color:#6b7280; font-weight:600;">#</th>
								<th style="padding:0.4rem; text-align:left; color:#111827; font-weight:600;">Campaign Name</th>
							</tr>
						</thead>
						<tbody id="campaign-table-body"></tbody>
					</table>
				</div>
			</div>
		`

		let detachHandlers = null

		const result = await Swal.fire({
			title: 'Filter by campaign',
			html: popupHtml,
			width: 600,
			showCancelButton: true,
			showDenyButton: true,
			confirmButtonText: 'Apply Filter',
			denyButtonText: 'Clear Filter',
			cancelButtonText: 'Close',
			focusConfirm: false,
			didOpen: () => {
				const popup = Swal.getPopup()
				const searchInput = popup.querySelector('#campaign-search')
				const tbody = popup.querySelector('#campaign-table-body')
				const selectedLabel = popup.querySelector('#campaign-selected-label')

				if (!searchInput || !tbody || !selectedLabel) return

				const updateSelectedLabel = () => {
					selectedLabel.textContent = pendingSelection ? `Selected: ${pendingSelection}` : 'Selected: none'
				}

				const renderRows = () => {
					const filtered = filterCampaigns(searchInput.value.trim())
					tbody.innerHTML = buildRows(filtered)
				}

				const handleRowClick = event => {
					const row = event.target.closest('tr[data-value]')
					if (!row) return
					pendingSelection = decodeURIComponent(row.getAttribute('data-value'))
					renderRows()
					updateSelectedLabel()
				}

				renderRows()
				updateSelectedLabel()

				searchInput.addEventListener('input', renderRows)
				tbody.addEventListener('click', handleRowClick)

				detachHandlers = () => {
					searchInput.removeEventListener('input', renderRows)
					tbody.removeEventListener('click', handleRowClick)
				}
			},
			willClose: () => {
				detachHandlers?.()
				detachHandlers = null
			},
			preConfirm: () => pendingSelection,
		})

		if (result.isDenied) {
			setSelectedCampaign('')
			updateFilters({ campaign: null })
			return
		}

		if (result.isConfirmed) {
			const value = result.value || ''
			setSelectedCampaign(value)
			updateFilters(value ? { campaign: value } : { campaign: null })
		}
	}, [campaignError, campaigns, loadCampaigns, loadingCampaigns, selectedCampaign, updateFilters])

	return {
		selectedCampaign,
		handleCampaignFilter,
		loadingCampaigns,
		campaignError,
		refreshCampaigns: loadCampaigns,
	}
}
